import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaWhatsapp, FaTelegramPlane, FaCrown } from "react-icons/fa";
import { IoGameController } from "react-icons/io5";
import { SiKick } from "react-icons/si";
import {
  LuTarget,
  LuChevronRight,
  LuLayoutGrid,
  LuBuilding2,
  LuBadgeCheck,
  LuRadio,
  LuLanguages,
  LuMail,
} from "react-icons/lu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const VG_OFFICIAL_HREF = "https://vgjogo7.com/?ch=160000&ic=1251328";
const XG_OFFICIAL_HREF = "https://xgjogo2.com/register/?ch=190000";
const TELEGRAM_HREF = "https://t.me/JogoMiriamSilva";

const PLATFORMS = [
  { code: "XG", href: XG_OFFICIAL_HREF, featured: true, isNew: true },
  { code: "VG", href: VG_OFFICIAL_HREF, featured: true },
  { code: "DG", href: "https://www.dgjogo6.com/?ch=740003&ic=601072209#/register" },
  { code: "YG", href: "https://ygjogo6.com/?ch=550003&ic=601077266#/register" },
  { code: "MG", href: "https://mgjogo6.com/?ch=350004&ic=140484180#/register" },
  { code: "HG", href: "https://hgjogo666.com/?ch=110003&ic=140493088#/register" },
  { code: "EG", href: "https://egjogo6.com/?ch=380002&ic=140437835#/register" },
  { code: "FG", href: "https://fgjogo6.com/?ch=230002&ic=140493455#/register" },
  { code: "WG", href: "http://wgpixel.wgjogo6.com/?referralCode=rcx5176" },
  { code: "BG", href: "https://www.bgjogo6.com/register?r=pga0908" },
  { code: "AG", href: "http://agpixel.agjogo6.com/?referralCode=ikh1032" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 22 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 280, damping: 26 },
  },
};

const SecondaryLink = ({
  href,
  testId,
  icon: Icon,
  label,
  accentClass,
  iconBg,
}) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    variants={itemVariants}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`group relative flex w-full items-center rounded-2xl border ${accentClass} backdrop-blur-xl py-4 px-4 transition-colors`}
  >
    <span
      className={`flex h-10 w-10 items-center justify-center rounded-xl ${iconBg} shrink-0`}
    >
      <Icon className="text-xl" />
    </span>
    <span className="flex-1 text-center font-semibold tracking-wide text-[15px] sm:text-base pr-10">
      {label}
    </span>
  </motion.a>
);

export default function LinkTree() {
  const [recruitLang, setRecruitLang] = useState("pt");
  const recruitContent = {
    pt: {
      title: "Recrutamento de Streamers na Kick",
      body: "Trabalho com recrutamento e parcerias para streamers já ativos. Se você já faz lives e quer crescer na plataforma, entre em contato comigo para oportunidades.",
      cta: "Me chame para saber mais",
      tag: "Oportunidade",
    },
    en: {
      title: "Streamer Recruitment on Kick",
      body: "I work with recruitment and partnerships for active streamers. If you are already streaming and want to grow on the platform, contact me for opportunities.",
      cta: "Message me to learn more",
      tag: "Opportunity",
    },
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden text-white">
      {/* Animated mesh background */}
      <div className="bg-mesh" aria-hidden />
      <div className="bg-grid" aria-hidden />
      <div className="orb orb-1" aria-hidden />
      <div className="orb orb-2" aria-hidden />

      {/* Diagonal light beams */}
      <div className="bg-beam b1" aria-hidden />
      <div className="bg-beam b2" aria-hidden />
      <div className="bg-beam b3" aria-hidden />

      {/* Twinkling stars */}
      <span className="bg-star t1" aria-hidden />
      <span className="bg-star t2" aria-hidden />
      <span className="bg-star t3" aria-hidden />
      <span className="bg-star t4" aria-hidden />
      <span className="bg-star t5" aria-hidden />
      <span className="bg-star t6" aria-hidden />
      <span className="bg-star t7" aria-hidden />
      <span className="bg-star t8" aria-hidden />
      <span className="bg-star t9" aria-hidden />
      <span className="bg-star t10" aria-hidden />

      {/* Rising gold particles */}
      <span className="bg-particle p1" aria-hidden />
      <span className="bg-particle p2" aria-hidden />
      <span className="bg-particle p3" aria-hidden />
      <span className="bg-particle p4" aria-hidden />
      <span className="bg-particle p5" aria-hidden />
      <span className="bg-particle p6" aria-hidden />
      <span className="bg-particle p7" aria-hidden />
      <span className="bg-particle p8" aria-hidden />
      <span className="bg-particle p9" aria-hidden />
      <span className="bg-particle p10" aria-hidden />
      <span className="bg-particle p11" aria-hidden />
      <span className="bg-particle p12" aria-hidden />
      {/* Vignette for legibility */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.55) 75%, rgba(0,0,0,0.85) 100%)",
        }}
        aria-hidden
      />
      {/* Grain */}
      <div className="grain" aria-hidden />

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-md flex-col px-5 py-10 sm:py-14">
        {/* === Profile Header (Recrutador Profissional) === */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="relative mb-2 flex items-center gap-4 rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0a0a0a]/80 via-[#120e07]/80 to-[#0a0a0a]/80 p-4 backdrop-blur-md"
          style={{
            boxShadow:
              "0 10px 40px -15px rgba(212,175,55,0.35), inset 0 0 0 1px rgba(212,175,55,0.06)",
          }}
          data-testid="profile-header"
        >
          {/* Avatar */}
          <div className="relative shrink-0">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full"
              style={{
                background:
                  "linear-gradient(135deg, #D4AF37 0%, #FFE17A 45%, #8B6914 100%)",
                boxShadow:
                  "0 0 18px rgba(212,175,55,0.45), inset 0 0 0 2px rgba(0,0,0,0.35)",
              }}
            >
              <SiKick className="text-2xl text-black" />
            </div>
            {/* Verified badge */}
            <span
              className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2"
              style={{
                background:
                  "linear-gradient(135deg, #FFF6D5 0%, #D4AF37 100%)",
                borderColor: "#0a0a0a",
              }}
              aria-label="Verificado"
            >
              <LuBadgeCheck className="text-[11px] text-black" strokeWidth={3} />
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h2
                className="truncate font-extrabold leading-tight tracking-wide text-[17px]"
                style={{
                  background:
                    "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 50%, #D4AF37 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Recrutador Oficial Kick
              </h2>
            </div>
            <p className="mt-0.5 truncate text-[12px] text-white/65">
              Parcerias &amp; Recrutamento de Streamers
            </p>
          </div>
        </motion.div>

        {/* Hero — Welcome logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 22 }}
          className="relative flex flex-col items-center text-center py-8"
          data-testid="hero-welcome"
        >
          {/* Decorative background layers */}
          <div className="hero-rays" aria-hidden />
          <div className="hero-aura" aria-hidden />

          {/* Floating sparkles */}
          <span className="sparkle s1" aria-hidden />
          <span className="sparkle s2" aria-hidden />
          <span className="sparkle s3" aria-hidden />
          <span className="sparkle s4" aria-hidden />
          <span className="sparkle s5" aria-hidden />
          <span className="sparkle s6" aria-hidden />

          {/* Crown */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 220, damping: 14 }}
            className="crown-bounce mb-2"
          >
            <FaCrown className="text-3xl text-[#FFD700]" />
          </motion.div>

          {/* Hero with side ornaments */}
          <div className="flex items-center gap-3 w-full">
            <motion.div
              className="hero-ornament"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              style={{ transformOrigin: "right" }}
              aria-hidden
            />

            {/* Title — letter by letter */}
            <h1
              className="hero-title text-[2.25rem] sm:text-5xl leading-none whitespace-nowrap"
              data-testid="hero-title"
              aria-label="Bem-vindos"
            >
              {"BEM-VINDOS".split("").map((char, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30, rotateX: -90 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  transition={{
                    delay: 0.3 + i * 0.07,
                    type: "spring",
                    stiffness: 260,
                    damping: 18,
                  }}
                  style={{ display: "inline-block" }}
                >
                  {char}
                </motion.span>
              ))}
            </h1>

            <motion.div
              className="hero-ornament"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              style={{ transformOrigin: "left" }}
              aria-hidden
            />
          </div>

          {/* Subtitle ornament — small diamond divider */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            className="mt-4 flex items-center gap-3"
          >
            <span className="block h-px w-8 bg-gradient-to-r from-transparent to-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs">◆</span>
            <span className="block h-px w-8 bg-gradient-to-l from-transparent to-[#D4AF37]" />
          </motion.div>
        </motion.div>

        {/* Buttons stack */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="mt-10 flex flex-col gap-4"
        >
          {/* === PLATAFORMA XG — NOVA (Hero CTA) === */}
          <motion.a
            href={XG_OFFICIAL_HREF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-xg-oficial"
            variants={itemVariants}
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.985 }}
            className="group relative flex w-full items-center gap-4 overflow-hidden rounded-2xl border px-4 py-5"
            style={{
              borderColor: "rgba(212,175,55,0.55)",
              background:
                "linear-gradient(135deg, #0a0a0a 0%, #15110a 50%, #0a0a0a 100%)",
              boxShadow:
                "0 0 0 1px rgba(212,175,55,0.18) inset, 0 18px 60px -18px rgba(212,175,55,0.55), 0 10px 36px -16px rgba(212,175,55,0.45)",
            }}
          >
            {/* "NOVA" pulsing badge */}
            <motion.span
              animate={{ scale: [1, 1.08, 1], opacity: [0.95, 1, 0.95] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-2 right-2 z-10 rounded-full border px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.22em]"
              style={{
                borderColor: "rgba(212,175,55,0.65)",
                background:
                  "linear-gradient(135deg, #FFE17A 0%, #D4AF37 100%)",
                color: "#0a0a0a",
                boxShadow: "0 0 14px rgba(255,225,122,0.55)",
              }}
            >
              ✨ Nova
            </motion.span>

            {/* Decorative glows — gold */}
            <div
              className="pointer-events-none absolute -top-16 -left-16 h-44 w-44 rounded-full opacity-50"
              style={{
                background:
                  "radial-gradient(circle, rgba(212,175,55,0.55) 0%, transparent 70%)",
                filter: "blur(28px)",
              }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-20 -right-16 h-52 w-52 rounded-full opacity-35"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,225,122,0.45) 0%, transparent 70%)",
                filter: "blur(32px)",
              }}
              aria-hidden
            />

            {/* Icon tile */}
            <span
              className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl"
              style={{
                background:
                  "linear-gradient(135deg, #FFE17A 0%, #D4AF37 55%, #8B6914 100%)",
                boxShadow:
                  "0 0 24px rgba(212,175,55,0.55), inset 0 0 0 2px rgba(0,0,0,0.35)",
              }}
            >
              <IoGameController className="text-3xl text-black" />
            </span>

            {/* Title */}
            <div className="relative flex flex-1 flex-col items-center text-center pr-2">
              <span
                className="font-extrabold leading-none tracking-wide text-[22px] sm:text-2xl"
                style={{
                  background:
                    "linear-gradient(180deg, #FFFBEB 0%, #FFF1A8 45%, #FFE17A 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.04em",
                  textShadow: "0 0 18px rgba(255,225,122,0.35)",
                }}
              >
                PLATAFORMA XG
              </span>
              <span
                className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.32em]"
                style={{ color: "#D4AF37" }}
              >
                Nova Plataforma
              </span>
            </div>
          </motion.a>

          {/* === PLATAFORMA VG — LINK OFICIAL (Hero CTA) === */}
          <motion.a
            href={VG_OFFICIAL_HREF}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="link-vg-oficial"
            variants={itemVariants}
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.985 }}
            className="group relative flex w-full items-center gap-4 overflow-hidden rounded-2xl border px-4 py-5"
            style={{
              borderColor: "rgba(212,175,55,0.55)",
              background:
                "linear-gradient(135deg, #0a0a0a 0%, #15110a 50%, #0a0a0a 100%)",
              boxShadow:
                "0 0 0 1px rgba(212,175,55,0.18) inset, 0 18px 60px -18px rgba(212,175,55,0.55), 0 10px 36px -16px rgba(212,175,55,0.45)",
            }}
          >
            {/* Decorative glows — gold only */}
            <div
              className="pointer-events-none absolute -top-16 -left-16 h-44 w-44 rounded-full opacity-50"
              style={{
                background:
                  "radial-gradient(circle, rgba(212,175,55,0.55) 0%, transparent 70%)",
                filter: "blur(28px)",
              }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute -bottom-20 -right-16 h-52 w-52 rounded-full opacity-35"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,225,122,0.45) 0%, transparent 70%)",
                filter: "blur(32px)",
              }}
              aria-hidden
            />

            {/* Icon tile */}
            <span
              className="relative flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl"
              style={{
                background:
                  "linear-gradient(135deg, #FFE17A 0%, #D4AF37 55%, #8B6914 100%)",
                boxShadow:
                  "0 0 24px rgba(212,175,55,0.55), inset 0 0 0 2px rgba(0,0,0,0.35)",
              }}
            >
              <IoGameController className="text-3xl text-black" />
            </span>

            {/* Title */}
            <div className="relative flex flex-1 flex-col items-center text-center pr-2">
              <span
                className="font-extrabold leading-none tracking-wide text-[22px] sm:text-2xl"
                style={{
                  background:
                    "linear-gradient(180deg, #FFFBEB 0%, #FFF1A8 45%, #FFE17A 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "0.04em",
                  textShadow: "0 0 18px rgba(255,225,122,0.35)",
                }}
              >
                PLATAFORMA VG
              </span>
              <span
                className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.32em]"
                style={{ color: "#D4AF37" }}
              >
                Link Oficial
              </span>
            </div>
          </motion.a>

          {/* === Grupo Telegram === */}
          <SecondaryLink
            href={TELEGRAM_HREF}
            testId="link-telegram-group"
            icon={FaTelegramPlane}
            label="Telegram"
            accentClass="border-[#229ED9]/25 bg-white/[0.03] text-white/85 hover:border-[#229ED9]/55 hover:bg-white/[0.06]"
            iconBg="bg-[#229ED9] text-white"
          />

          {/* === WhatsApp Group Oficial === */}
          <SecondaryLink
            href="https://chat.whatsapp.com/DEQwcUnGVot2bYoAceUbvu"
            testId="link-whatsapp-group-oficial"
            icon={FaWhatsapp}
            label="Grupo Oficial"
            accentClass="border-[#D4AF37]/20 bg-white/[0.03] text-white/85 hover:border-[#D4AF37]/45 hover:bg-white/[0.06]"
            iconBg="bg-[#25D366] text-black"
          />

          {/* === WhatsApp Fale comigo === */}
          <SecondaryLink
            href="https://wa.me/5518991604679"
            testId="link-whatsapp-contact"
            icon={FaWhatsapp}
            label="Fale comigo"
            accentClass="border-[#D4AF37]/20 bg-white/[0.03] text-white/85 hover:border-[#D4AF37]/45 hover:bg-white/[0.06]"
            iconBg="bg-[#25D366] text-black"
          />

          {/* === Kick Streamer Recruitment Card (PT/EN toggle) === */}
          <motion.div variants={itemVariants} className="w-full pt-2">
            <div
              className="relative overflow-hidden rounded-2xl border p-5"
              data-testid="kick-recruit-card"
              style={{
                background:
                  "linear-gradient(135deg, #0a0a0a 0%, #0d1208 45%, #0a0a0a 100%)",
                borderColor: "rgba(83, 252, 24, 0.35)",
                boxShadow:
                  "0 10px 40px -15px rgba(83,252,24,0.35), inset 0 0 0 1px rgba(212,175,55,0.08)",
              }}
            >
              {/* Decorative Kick-green glow */}
              <div
                className="pointer-events-none absolute -top-16 -left-16 h-44 w-44 rounded-full opacity-40"
                style={{
                  background:
                    "radial-gradient(circle, rgba(83,252,24,0.55) 0%, transparent 70%)",
                  filter: "blur(28px)",
                }}
                aria-hidden
              />
              {/* Decorative gold accent */}
              <div
                className="pointer-events-none absolute -bottom-20 -right-20 h-48 w-48 rounded-full opacity-30"
                style={{
                  background:
                    "radial-gradient(circle, rgba(212,175,55,0.55) 0%, transparent 70%)",
                  filter: "blur(32px)",
                }}
                aria-hidden
              />

              {/* Header: icon + title + language toggle */}
              <div className="relative flex items-start gap-3">
                <span
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                  style={{
                    background:
                      "linear-gradient(135deg, #53FC18 0%, #2bb800 100%)",
                    boxShadow: "0 0 18px rgba(83, 252, 24, 0.45)",
                  }}
                >
                  <SiKick className="text-2xl text-black" />
                </span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-[#53FC18]/40 bg-[#53FC18]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#53FC18]"
                    >
                      🎥 KICK
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#D4AF37]">
                      {recruitContent[recruitLang].tag}
                    </span>
                  </div>
                  <h3
                    className="mt-1.5 font-extrabold leading-tight text-[17px]"
                    style={{
                      background:
                        "linear-gradient(180deg, #FFF6D5 0%, #FFE17A 50%, #D4AF37 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {recruitContent[recruitLang].title}
                  </h3>
                </div>

                {/* Language toggle */}
                <div
                  className="flex shrink-0 items-center gap-0.5 rounded-full border border-[#D4AF37]/25 bg-black/40 p-0.5 backdrop-blur-sm"
                  role="tablist"
                  aria-label="Idioma"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={recruitLang === "pt"}
                    onClick={() => setRecruitLang("pt")}
                    data-testid="recruit-lang-pt"
                    className={`relative rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-all ${
                      recruitLang === "pt"
                        ? "text-black shadow-[0_0_10px_rgba(212,175,55,0.45)]"
                        : "text-white/60 hover:text-white"
                    }`}
                    style={
                      recruitLang === "pt"
                        ? {
                            background:
                              "linear-gradient(135deg, #FFE17A 0%, #D4AF37 100%)",
                          }
                        : undefined
                    }
                  >
                    PT
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={recruitLang === "en"}
                    onClick={() => setRecruitLang("en")}
                    data-testid="recruit-lang-en"
                    className={`relative rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider transition-all ${
                      recruitLang === "en"
                        ? "text-black shadow-[0_0_10px_rgba(212,175,55,0.45)]"
                        : "text-white/60 hover:text-white"
                    }`}
                    style={
                      recruitLang === "en"
                        ? {
                            background:
                              "linear-gradient(135deg, #FFE17A 0%, #D4AF37 100%)",
                          }
                        : undefined
                    }
                  >
                    EN
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="relative mt-4 min-h-[72px]">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={recruitLang}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.25 }}
                    className="text-[13.5px] leading-relaxed text-white/80"
                  >
                    {recruitContent[recruitLang].body}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* CTA Button */}
              <motion.a
                href="https://wa.me/5518991604679"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="recruit-cta-whatsapp"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="relative mt-4 flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl px-4 py-3 font-bold tracking-wide"
                style={{
                  background:
                    "linear-gradient(135deg, #53FC18 0%, #2bb800 100%)",
                  color: "#031200",
                  boxShadow:
                    "0 8px 24px -8px rgba(83,252,24,0.55), inset 0 0 0 1px rgba(255,255,255,0.18)",
                }}
              >
                <FaWhatsapp className="text-lg" />
                <span className="text-[13.5px]">
                  {recruitContent[recruitLang].cta}
                </span>
                <LuChevronRight className="text-base" />
              </motion.a>

              {/* Bottom hint: shows the alternate language as a tease */}
              <div className="relative mt-2 flex items-center justify-center gap-1.5 text-[10.5px] text-white/40">
                <LuLanguages className="text-xs" />
                <span>
                  {recruitLang === "pt"
                    ? "Available in English — tap EN"
                    : "Disponível em Português — toque PT"}
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="mt-auto pt-12 text-center"
          data-testid="page-footer"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/30">
            Todos os direitos reservados
          </p>
        </motion.footer>
      </div>
    </div>
  );
}
